import {
  getTruckCapacity,
  getTruckDate,
  getTruckId,
  getTruckPallets,
  getTruckRegion,
} from './fboSupplyPlanTruckRows.mjs';

export const FBO_TRUCK_SORT = Object.freeze({
  DATE_ASC: 'date_asc',
  DATE_DESC: 'date_desc',
  REGION: 'region',
  UNDERLOADED: 'underloaded',
  FILLED: 'filled',
});

export const FBO_TRUCK_STATUS = Object.freeze({
  ALL: 'all',
  FULL: 'full',
  UNDERLOADED: 'underloaded',
  OVERLOADED: 'overloaded',
});

const compareText = (left, right) => String(left ?? '').localeCompare(String(right ?? ''), 'ru', {
  numeric: true,
  sensitivity: 'base',
});

const truckItems = (truck) => truck?.items || truck?.composition || truck?.rows || [];
const normalizedSearch = (value) => String(value ?? '').trim().toLocaleLowerCase('ru');

const validNumber = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function getFboTruckLoadMeta(truck) {
  const capacity = validNumber(getTruckCapacity(truck));
  const pallets = validNumber(getTruckPallets(truck));
  let status = FBO_TRUCK_STATUS.UNDERLOADED;

  if (capacity != null && pallets != null) {
    if (pallets > capacity) status = FBO_TRUCK_STATUS.OVERLOADED;
    else if (pallets === capacity) status = FBO_TRUCK_STATUS.FULL;
  } else if (truck?.status === 'overfill' || truck?.status === 'overloaded') {
    status = FBO_TRUCK_STATUS.OVERLOADED;
  } else if (truck?.status === 'full') {
    status = FBO_TRUCK_STATUS.FULL;
  }

  const free = capacity != null && pallets != null ? capacity - pallets : null;
  const percent = capacity != null && capacity > 0 && pallets != null
    ? (pallets / capacity) * 100
    : null;
  return { capacity, pallets, free, percent, status };
}

export function matchFboTruckSearch(truck, query) {
  const needle = normalizedSearch(query);
  if (!needle) return { matches: true, matchedInItems: false };

  const date = getTruckDate(truck);
  const dateLabel = date
    ? new Date(`${String(date).slice(0, 10)}T00:00:00`).toLocaleDateString('ru-RU')
    : '';
  const truckMatch = [
    getTruckRegion(truck),
    date,
    dateLabel,
  ].some((value) => normalizedSearch(value).includes(needle));

  const itemMatch = truckItems(truck).some((item) => [
    item?.article,
    item?.display_name,
    item?.name,
    item?.nm_id,
    item?.product_key,
    item?.barcode,
    item?.tech_size,
  ].some((value) => normalizedSearch(value).includes(needle)));

  return { matches: truckMatch || itemMatch, matchedInItems: !truckMatch && itemMatch };
}

export const getFboTruckListKey = (truck, index = 0) => {
  const id = getTruckId(truck);
  if (id != null && String(id) !== '') return `id:${id}`;
  return `draft:${getTruckDate(truck) || ''}:${getTruckRegion(truck) || ''}:${index}`;
};

export function sortFboTrucks(trucks = [], regionOrder = [], sortBy = FBO_TRUCK_SORT.DATE_ASC) {
  const regionRanks = new Map(
    regionOrder
      .map((region) => (typeof region === 'object' ? region?.value ?? region?.okrug : region))
      .filter(Boolean)
      .map((region, index) => [String(region), index]),
  );

  return trucks
    .map((truck, index) => ({ truck, index }))
    .sort((left, right) => {
      const leftRegion = String(getTruckRegion(left.truck) || '');
      const rightRegion = String(getTruckRegion(right.truck) || '');
      const dateComparison = compareText(getTruckDate(left.truck), getTruckDate(right.truck));
      const regionComparison = compareText(leftRegion, rightRegion);
      const leftLoad = getFboTruckLoadMeta(left.truck);
      const rightLoad = getFboTruckLoadMeta(right.truck);

      let primary = 0;
      if (sortBy === FBO_TRUCK_SORT.DATE_DESC) {
        primary = -dateComparison;
      } else if (sortBy === FBO_TRUCK_SORT.REGION) {
        primary = regionComparison;
      } else if (sortBy === FBO_TRUCK_SORT.UNDERLOADED) {
        const statusRank = {
          [FBO_TRUCK_STATUS.UNDERLOADED]: 0,
          [FBO_TRUCK_STATUS.FULL]: 1,
          [FBO_TRUCK_STATUS.OVERLOADED]: 2,
        };
        primary = statusRank[leftLoad.status] - statusRank[rightLoad.status];
        if (!primary) {
          const leftFree = leftLoad.free == null ? Number.NEGATIVE_INFINITY : leftLoad.free;
          const rightFree = rightLoad.free == null ? Number.NEGATIVE_INFINITY : rightLoad.free;
          primary = rightFree - leftFree;
        }
      } else if (sortBy === FBO_TRUCK_SORT.FILLED) {
        const statusRank = {
          [FBO_TRUCK_STATUS.FULL]: 0,
          [FBO_TRUCK_STATUS.OVERLOADED]: 1,
          [FBO_TRUCK_STATUS.UNDERLOADED]: 2,
        };
        primary = statusRank[leftLoad.status] - statusRank[rightLoad.status];
        if (!primary) {
          const leftPercent = leftLoad.percent == null ? Number.NEGATIVE_INFINITY : leftLoad.percent;
          const rightPercent = rightLoad.percent == null ? Number.NEGATIVE_INFINITY : rightLoad.percent;
          primary = rightPercent - leftPercent;
        }
      } else {
        primary = dateComparison;
      }
      if (primary) return primary;

      if (sortBy !== FBO_TRUCK_SORT.DATE_DESC && dateComparison) return dateComparison;
      if (sortBy === FBO_TRUCK_SORT.DATE_DESC && dateComparison) return -dateComparison;

      const leftRank = regionRanks.get(leftRegion);
      const rightRank = regionRanks.get(rightRegion);
      if (leftRank != null || rightRank != null) {
        const rankComparison = (leftRank ?? Number.MAX_SAFE_INTEGER) - (rightRank ?? Number.MAX_SAFE_INTEGER);
        if (rankComparison) return rankComparison;
      }

      if (regionComparison) return regionComparison;

      const idComparison = compareText(getTruckId(left.truck), getTruckId(right.truck));
      return idComparison || left.index - right.index;
    })
    .map(({ truck }) => truck);
}

export function prepareFboTruckRows(trucks = [], {
  regionOrder = [],
  query = '',
  status = FBO_TRUCK_STATUS.ALL,
  region = '',
  sortBy = FBO_TRUCK_SORT.DATE_ASC,
} = {}) {
  const matches = new Map();
  const keyCounts = new Map();
  const rowKeys = new Map();
  trucks.forEach((truck, index) => {
    const baseKey = getFboTruckListKey(truck, index);
    const occurrence = keyCounts.get(baseKey) || 0;
    keyCounts.set(baseKey, occurrence + 1);
    rowKeys.set(truck, occurrence ? `${baseKey}:duplicate:${occurrence}` : baseKey);
  });
  const filtered = trucks.filter((truck) => {
    if (region && String(getTruckRegion(truck) || '') !== String(region)) return false;
    if (status !== FBO_TRUCK_STATUS.ALL && getFboTruckLoadMeta(truck).status !== status) return false;
    const match = matchFboTruckSearch(truck, query);
    matches.set(truck, match);
    return match.matches;
  });

  return sortFboTrucks(filtered, regionOrder, sortBy).map((truck, index) => ({
    truck,
    key: rowKeys.get(truck) || getFboTruckListKey(truck, index),
    matchedInItems: Boolean(matches.get(truck)?.matchedInItems),
  }));
}
